/* ------------------------------------------------------------------ */
/*  HTML-текст из API → простой текст: описания страниц и JSON-LD     */
/* ------------------------------------------------------------------ */

// Именованные сущности из текстов админки (CKEditor): кавычки, тире, неразрывный
// пробел и соседняя типографика. Незнакомая сущность остаётся как есть.
const NAMED_ENTITIES: Record<string, string> = {
  nbsp: ' ',
  thinsp: ' ',
  ensp: ' ',
  emsp: ' ',
  shy: '',
  amp: '&',
  quot: '"',
  apos: "'",
  laquo: '«',
  raquo: '»',
  bdquo: '„',
  ldquo: '“',
  rdquo: '”',
  sbquo: '‚',
  lsquo: '‘',
  rsquo: '’',
  mdash: '—',
  ndash: '–',
  minus: '−',
  hellip: '…',
  asymp: '≈',
  times: '×',
  plusmn: '±',
  deg: '°',
  middot: '·',
  bull: '•',
  numero: '№',
  euro: '€',
  copy: '©',
  reg: '®',
  trade: '™',
}

// &lt; и &gt; (и их числовые формы) сознательно не раскодируем: stripHtml кормит и
// JSON-LD, который страницы вставляют в <script> без экранирования, — раскодированные
// скобки могли бы закрыть тег. В текстах API их нет (проверено 25.09.2026).
const decodeEntities = (s: string): string =>
  s.replace(
    /&(?:#(\d{1,7})|#x([0-9a-f]{1,6})|([a-z]+));/gi,
    (entity: string, dec?: string, hex?: string, name?: string) => {
      if (name !== undefined) {
        // typeof, а не ??: у объекта есть унаследованные ключи (&constructor;)
        const char = NAMED_ENTITIES[name]
        return typeof char === 'string' ? char : entity
      }
      const code =
        dec !== undefined
          ? parseInt(dec, 10)
          : parseInt(hex ?? '', 16)
      if (
        code === 60 ||
        code === 62 ||
        code > 0x10ffff ||
        (code >= 0xd800 && code <= 0xdfff)
      )
        return entity
      return code < 32 || (code >= 127 && code < 160)
        ? ' '
        : String.fromCodePoint(code)
    }
  )

// Теги внутри фразы заменяем пробелом (иначе слипаются слова), раскодируем
// сущности, схлопываем пробелы и убираем те, что тег оставил у знаков
// препинания: «<strong>ТСП</strong>.» → «ТСП.».
const inlineText = (s: string): string =>
  decodeEntities(s.replace(/<[^>]+>/g, ' '))
    .replace(/\s+/g, ' ')
    .replace(/ ([.,;:!?»)])/g, '$1')
    .replace(/([«(]) /g, '$1')
    .trim()

// Конец абзаца, заголовка, пункта списка, <br> (в текстах админки бывает с
// атрибутами) и перевод строки вне тега — граница фразы: иначе «Дата: 18 июля</p>
// <p>Гостей: 50» слиплось бы в «Дата: 18 июля Гостей: 50». Точку ставим, только
// если у фразы нет своего знака, а следующая начинается с заглавной или цифры:
// перенос посреди фразы остаётся пробелом.
const BLOCK_END = /<\/(?:p|h[1-6]|div|li)\s*>|<br\b[^>]*>|\n/gi
const joinPhrases = (text: string, next: string): string =>
  !text
    ? next
    : /[.!?…:;,—–-]$/.test(text) || !/^[A-ZА-ЯЁ0-9«"„(]/.test(next)
    ? `${text} ${next}`
    : `${text}. ${next}`

export const stripHtml = (s: string | null | undefined): string =>
  (s ?? '')
    .replace(/<[^>]*>/g, (tag) => tag.replace(/\s+/g, ' ')) // <a\nhref> — один тег
    .split(BLOCK_END)
    .map(inlineText)
    .filter(Boolean)
    .reduce(joinPhrases, '')

// Висящие в конце обрезки знаки и короткое слово: предлог, союз или заглавная
// начала следующей фразы («…живых цветов в», «…2025. В»). Единицу после числа
// («выход 300 г») не трогаем.
const DANGLING_MARKS = /[\s,;:(«"„–—-]+$/
const DANGLING_WORD = /([^\d\s])\s(?:[а-яёa-z]{1,2}|[А-ЯЁA-Z])$/

// Описание страницы для <meta name="description"> из HTML-текста: не длиннее max
// символов (поисковик показывает около 160), обрезка по границе слова; оборвали
// фразу — многоточие, закончили на точке — без него.
export const metaDescription = (
  html: string | null | undefined,
  max = 160
): string => {
  const text = stripHtml(html)
  const chars = Array.from(text) // символы, а не UTF-16: эмодзи не разрезаем
  if (chars.length <= max) return text

  let cut = chars.slice(0, max - 1).join('') // одно место — под «…»
  if (!/\s/.test(chars[max - 1] ?? '')) {
    const space = cut.lastIndexOf(' ')
    if (space > 0) cut = cut.slice(0, space)
  }
  cut = cut.trimEnd()
  if (/[.!?…]$/.test(cut)) return cut

  cut = cut
    .replace(DANGLING_MARKS, '')
    .replace(DANGLING_WORD, '$1')
    .replace(DANGLING_MARKS, '')
  return /[.!?…]$/.test(cut) ? cut : `${cut}…`
}
